import React from "react";
import { AlertCircle, Download, Info } from "lucide-react";
import type { Store } from "../../types/store";
import { CSV_SCHEMA } from "./schema";
import { downloadTemplate } from "./utils";

interface Props {
  stores: Store[];
  selectedStore: string;
  onStoreChange: (storeId: string) => void;
  onFileSelected: (file: File) => void;
}

const StepUpload: React.FC<Props> = ({ stores, selectedStore, onStoreChange, onFileSelected }) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelected(file);
  };

  return (
    <div>
      <div className="mb-4 text-left">
        <label className="form-label">Select Store:</label>
        <select
          className="form-input"
          value={selectedStore}
          onChange={(e) => onStoreChange(e.target.value)}
        >
          <option value="">-- Choose a store --</option>
          {stores.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      <p className="mb-3 form-label">
        Upload a CSV or Excel file containing your product list.
      </p>

      {!selectedStore && (
        <div className="flex items-center gap-2 mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-md">
          <AlertCircle size={15} className="text-amber-600 flex-shrink-0" />
          <p className="text-xs text-amber-700">Please select a store before uploading a file.</p>
        </div>
      )}

      <input
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileChange}
        disabled={!selectedStore}
        className="block w-full border p-3 rounded-md form-input mb-5 disabled:opacity-40 disabled:cursor-not-allowed"
      />

      {/* Info / Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
        <div className="flex items-center gap-2 mb-3">
          <Info size={16} className="text-blue-600 flex-shrink-0" />
          <span className="text-sm font-semibold text-blue-800">CSV / Excel Format Guide</span>
        </div>

        <p className="text-xs text-blue-700 mb-3">
          Your file can use any column names — you will map them to the fields below in the next
          step. Use the template for a ready-to-fill file.
        </p>

        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-blue-100 text-blue-900">
                <th className="text-left p-2 border border-blue-200">Field</th>
                <th className="text-left p-2 border border-blue-200">Required</th>
                <th className="text-left p-2 border border-blue-200">Type</th>
                <th className="text-left p-2 border border-blue-200">Format / Constraints</th>
                <th className="text-left p-2 border border-blue-200">Example</th>
              </tr>
            </thead>
            <tbody>
              {CSV_SCHEMA.map((col) => (
                <tr key={col.field} className="border-b border-blue-100">
                  <td className="p-2 border border-blue-200 font-mono font-medium">{col.field}</td>
                  <td className="p-2 border border-blue-200">
                    {col.required ? (
                      <span className="inline-block bg-red-100 text-red-700 text-xs font-semibold px-1.5 py-0.5 rounded">
                        Required
                      </span>
                    ) : (
                      <span className="inline-block bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded">
                        Optional
                      </span>
                    )}
                  </td>
                  <td className="p-2 border border-blue-200 text-gray-700">{col.type}</td>
                  <td className="p-2 border border-blue-200 text-gray-600">{col.format}</td>
                  <td className="p-2 border border-blue-200 font-mono text-gray-700">{col.example}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex items-start gap-2">
          <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            Monetary fields (cost_price, selling_price) must be plain numbers — no currency symbols
            ($, €, ₵, etc.) or thousand separators. The system will attempt to auto-strip them, but
            invalid values will be reported as errors.
          </p>
        </div>

        <button
          onClick={downloadTemplate}
          className="mt-3 flex items-center gap-1.5 text-xs text-blue-700 underline hover:text-blue-900"
        >
          <Download size={13} />
          Download sample CSV template
        </button>
      </div>
    </div>
  );
};

export default StepUpload;
